import { useState, useRef, useEffect } from "react";
import { Upload, UploadProps } from "antd";
import { useAuth } from "../context/AuthContext";
import { getToken } from "./api";
import {
  trackFileUploadStart,
  trackFileUploadSuccess,
  trackFileUploadError,
  trackAnalysisStart,
  trackAnalysisComplete,
  trackAnalysisError,
} from "../analytics";

const MAX_UPLOAD_SIZE = parseInt(
  process.env.REACT_APP_MAX_UPLOAD_SIZE || "104857600",
  10,
);

export interface UseFileUploadResult {
  uploadProps: UploadProps;
  uploadKey: number;
  showProgress: boolean;
  isAnalysing: boolean;
  progressPercentage: number;
  showSubscribeModal: boolean;
  setShowSubscribeModal: (v: boolean) => void;
}

export function useFileUpload(
  onSuccess: (analysisId: string) => void,
): UseFileUploadResult {
  const { isAuthenticated } = useAuth();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [uploadKey, setUploadKey] = useState(0);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const pendingAnalysisId = useRef<string | null>(null);

  useEffect(() => {
    if (!showSubscribeModal && pendingAnalysisId.current) {
      const id = pendingAnalysisId.current;
      pendingAnalysisId.current = null;
      onSuccess(id);
    }
  }, [showSubscribeModal, onSuccess]);

  const uploadProps: UploadProps = {
    action: () => {
      const uploadsPrivate = localStorage.getItem("uploadsPrivate") === "true";
      return uploadsPrivate ? "/api/analyse?is_public=false" : "/api/analyse";
    },
    showUploadList: false,
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},

    beforeUpload: async (file) => {
      if (file.size === 0) {
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        return Upload.LIST_IGNORE;
      }
      const token = await getToken();
      setAuthToken(token || null);
      return true;
    },

    onChange(info) {
      switch (info.file?.status) {
        case "uploading":
          if (progressPercentage === 0) {
            trackFileUploadStart(info.file.size || 0);
            trackAnalysisStart();
          }
          setShowProgress(true);
          setProgressPercentage((prev) =>
            Math.max(prev, info.file.percent || 0),
          );
          break;
        case "done":
          setProgressPercentage(100);
          setShowProgress(false);
          setIsAnalysing(true);
          trackFileUploadSuccess(info.file.size || 0);
          trackAnalysisComplete();

          const count = localStorage.getItem("uploadCount");
          let willShowModal = false;
          if (count) {
            const next = parseInt(count, 10) + 1;
            localStorage.setItem("uploadCount", next.toString());
            if (next % 2 === 0 && !isAuthenticated) {
              willShowModal = true;
              setShowSubscribeModal(true);
            }
          } else {
            localStorage.setItem("uploadCount", "1");
          }

          const analysisId = info.file.response.analysis_id;
          if (willShowModal) {
            pendingAnalysisId.current = analysisId;
            setTimeout(() => {
              setIsAnalysing(false);
              setProgressPercentage(0);
              setUploadKey((k) => k + 1);
            }, 1500);
          } else {
            setTimeout(() => {
              setIsAnalysing(false);
              setProgressPercentage(0);
              setUploadKey((k) => k + 1);
              onSuccess(analysisId);
            }, 1500);
          }
          break;
        case "error":
          setShowProgress(false);
          setIsAnalysing(false);
          setProgressPercentage(0);
          setUploadKey((k) => k + 1);
          trackFileUploadError(info.file.response?.Message || "Upload failed");
          trackAnalysisError(info.file.response?.Message || "Analysis failed");
          break;
      }
    },
  };

  return {
    uploadProps,
    uploadKey,
    showProgress,
    isAnalysing,
    progressPercentage,
    showSubscribeModal,
    setShowSubscribeModal,
  };
}
